import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { DueDiligenceTier, dueDiligenceTierSchema } from "../../../Common/Types-DTOs/DueDiligence";
import { BusinessCreditsDTO } from "../../../Common/Types-DTOs/ClientUser";
import { DueDiligencePlanOffer } from "../../../Common/Hooks/useApi/Schemas";
import { CREDIT_EURO_VALUE, formatMoney, planNameKey } from "../../../Common/Utils/dueDiligencePlans";
import { roundCredits } from "../../../Common/Utils/credits";
import { TierSelector } from "../Components/TierSelector";
import { DueDiligenceOneOffPrices, DueDiligencePlanPicker } from "../../../Components/DueDiligencePlanPicker";

const TIERS = dueDiligenceTierSchema.options;

export type DueDiligenceCreditCosts = Record<DueDiligenceTier, number>;

/** How the new case gets paid. */
export type NewCasePayment =
  | { kind: "credits" }
  | { kind: "plan"; lookupKey: string }
  | { kind: "oneOff" };

/**
 * Whether the business pays its DDs with credits: only with a paid plan. A
 * balance without one, gifted for instance, doesn't make it pay with credits.
 * @param credits - The business credits, when already read.
 * @returns True when the case is paid with credits, and only with them.
 */
export const paysWithCredits = (credits?: BusinessCreditsDTO): boolean => Boolean(credits?.dueDiligencePlan);

/**
 * The payment the modal comes to: no choosing between credits and euros.
 * @param credits - The business credits, when already read.
 * @param selectedPlan - The plan picked to subscribe to, if any.
 * @returns Credits for subscribers; else the picked plan; else one-off.
 */
export const resolveNewCasePayment = (
  credits: BusinessCreditsDTO | undefined,
  selectedPlan: string | null
): NewCasePayment => {
  if (paysWithCredits(credits)) {
    return { kind: "credits" };
  }
  if (selectedPlan) {
    return { kind: "plan", lookupKey: selectedPlan };
  }
  return { kind: "oneOff" };
};

/**
 * Why each tier can't be paid with credits, for the tiers that can't.
 * @param credits - The business balance and plan.
 * @param costs - What each tier costs in credits.
 * @param reasons - The texts: tiers the plan doesn't cover, or credits short.
 * @returns A reason per unavailable tier; the others are missing.
 */
export const tiersUnavailableWithCredits = (
  credits: BusinessCreditsDTO,
  costs: DueDiligenceCreditCosts,
  reasons: { notInPlan: () => string; missing: (credits: number) => string }
): Partial<Record<DueDiligenceTier, string>> => {
  const allowedTiers = credits.dueDiligencePlan?.allowedTiers;
  const unavailable: Partial<Record<DueDiligenceTier, string>> = {};
  for (const tier of TIERS) {
    if (allowedTiers && !allowedTiers.includes(tier)) {
      unavailable[tier] = reasons.notInPlan();
    } else if (costs[tier] > credits.balance) {
      unavailable[tier] = reasons.missing(roundCredits(costs[tier] - credits.balance));
    }
  }
  return unavailable;
};

/**
 * Whether credits pay a case of the tier: the plan covers it and the balance reaches.
 * @param credits - The business balance and plan.
 * @param costs - What each tier costs in credits; without them it can't be told.
 * @param tier - The tier of the case.
 * @returns False too while the costs are unknown, so nothing is paid blind.
 */
export const creditsPayTier = (
  credits: BusinessCreditsDTO,
  costs: DueDiligenceCreditCosts | null | undefined,
  tier: DueDiligenceTier
): boolean => {
  if (!costs) return false;
  const allowedTiers = credits.dueDiligencePlan?.allowedTiers;
  return (!allowedTiers || allowedTiers.includes(tier)) && costs[tier] <= credits.balance;
};

/**
 * Why each plan can't pay a case of the tier, for the plans that can't.
 * @param plans - The plans on offer.
 * @param tier - The tier of the case.
 * @param costs - What each tier costs in credits.
 * @param reasons - The texts: the plan doesn't cover the tier, or its month falls short.
 * @returns A reason per unavailable plan, by lookup key.
 */
export const plansUnavailableForTier = (
  plans: DueDiligencePlanOffer[],
  tier: DueDiligenceTier,
  costs: DueDiligenceCreditCosts,
  reasons: { notIncluded: string; notEnough: string }
): Record<string, string> => {
  const unavailable: Record<string, string> = {};
  for (const plan of plans) {
    if (plan.allowedTiers && !plan.allowedTiers.includes(tier)) {
      unavailable[plan.lookupKey] = reasons.notIncluded;
    } else if (plan.monthlyCredits < costs[tier]) {
      unavailable[plan.lookupKey] = reasons.notEnough;
    }
  }
  return unavailable;
};

const Savings = styled.span`
  font-weight: 700;
  color: #0D9488;
`;

const SUMMARY_TONES = {
  ok: { background: "#f0fdfa", color: "#115e59", border: "#ccfbf1" },
  warn: { background: "#fef3c7", color: "#92400e", border: "#fde68a" },
  info: { background: "#f9fafb", color: "#374151", border: "#e5e7eb" },
};

export type SummaryTone = keyof typeof SUMMARY_TONES;

/** What paying comes to, right before paying: green when it can, amber when it can't. */
export const SummaryBox = styled.div<{ $tone: SummaryTone }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: 10px;
  padding: 9px 12px;
  border-radius: 8px;
  font-size: 0.78rem;
  background-color: ${(p: { $tone: SummaryTone }) => SUMMARY_TONES[p.$tone].background};
  color: ${(p: { $tone: SummaryTone }) => SUMMARY_TONES[p.$tone].color};
  border: 1px solid ${(p: { $tone: SummaryTone }) => SUMMARY_TONES[p.$tone].border};
`;

export const SummaryActions = styled.span`
  display: flex;
  gap: 6px;
  white-space: nowrap;
`;

export const SummaryLink = styled.button`
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  font-weight: 700;
  color: inherit;
  text-decoration: underline;
  cursor: pointer;
`;

const SummaryLinkSeparator = styled.span`
  opacity: 0.5;
`;

const PlansTitle = styled.p`
  margin: 16px 0 8px;
  font-size: 0.78rem;
  font-weight: 600;
  color: #374151;
`;

interface PaymentSectionProps {
  tier: DueDiligenceTier;
  onTierChange: (tier: DueDiligenceTier) => void;
  pricing?: DueDiligenceOneOffPrices | null;
  creditCosts?: DueDiligenceCreditCosts | null;
  credits?: BusinessCreditsDTO;
  plans: DueDiligencePlanOffer[] | null;
  /** The payment the form resolved from the choices below. */
  payment: NewCasePayment;
  /** Leaves the modal for the plans page. */
  onChangePlan: () => void;
  /** Leaves the modal for Stripe, to buy the credits short. */
  onBuyCredits: () => void;
  onPlanChange: (lookupKey: string | null) => void;
  selectedPlan: string | null;
}

/**
 * The tier of the new case, priced the way it gets paid: in credits for
 * subscribers, otherwise one-off or subscribing to a plan.
 */
export const PaymentSection = ({
  tier,
  onTierChange,
  pricing,
  creditCosts,
  credits,
  plans,
  payment,
  onChangePlan,
  onBuyCredits,
  onPlanChange,
  selectedPlan,
}: PaymentSectionProps) => {
  const { t, i18n } = useTranslation();
  const withCredits = payment.kind === "credits" && credits && creditCosts;
  const money = (amount: number) => formatMoney(amount, "eur", i18n.language);

  // Each tier also tells the other way of paying: credits in euros and what
  // they save, or one-off in the credits a plan would take
  const notes: Partial<Record<DueDiligenceTier, ReactNode>> = {};
  for (const tierOption of TIERS) {
    const cost = creditCosts?.[tierOption];
    if (cost === undefined) continue;
    if (withCredits) {
      const equivalent = cost * CREDIT_EURO_VALUE;
      const oneOffPrice = pricing?.[tierOption];
      notes[tierOption] = (
        <>
          {t("dueDiligence.newCaseModal.payment.notes.equivalent", { amount: money(equivalent) })}
          {oneOffPrice != null && oneOffPrice > equivalent && (
            <>
              {" · "}
              <Savings>{t("dueDiligence.newCaseModal.payment.notes.savings", { amount: money(oneOffPrice - equivalent) })}</Savings>
            </>
          )}
        </>
      );
    } else {
      notes[tierOption] = t("dueDiligence.newCaseModal.payment.notes.withPlan", { count: cost });
    }
  }

  const summary = (() => {
    const cost = creditCosts?.[tier];
    const oneOffPrice = pricing?.[tier];

    if (withCredits && cost !== undefined) {
      const plan = credits.dueDiligencePlan;
      const reason = tiersUnavailableWithCredits(credits, creditCosts, {
        notInPlan: () =>
          t("dueDiligence.newCaseModal.payment.summary.notInPlan", { plan: plan ? t(planNameKey(plan.lookupKey)) : "" }),
        missing: (count) => t("dueDiligence.newCaseModal.payment.summary.missing", { count }),
      })[tier];

      if (!reason) {
        return (
          <SummaryBox $tone="ok">
            {t("dueDiligence.newCaseModal.payment.summary.deducted", { count: cost, left: roundCredits(credits.balance - cost) })}
          </SummaryBox>
        );
      }

      // Buying credits doesn't help with a tier the plan doesn't cover
      const planCoversTier = !plan?.allowedTiers || plan.allowedTiers.includes(tier);
      const renewal = plan
        ? t("dueDiligence.newCaseModal.payment.summary.renews", {
            date: new Date(plan.paidUntil).toLocaleDateString(i18n.language, { day: "numeric", month: "short" }),
          })
        : "";
      return (
        <SummaryBox $tone="warn">
          <span>{[reason, renewal].filter(Boolean).join(" ")}</span>
          <SummaryActions>
            {planCoversTier && (
              <>
                <SummaryLink type="button" onClick={onBuyCredits}>
                  {t("dueDiligence.newCaseModal.payment.summary.buyCredits")}
                </SummaryLink>
                <SummaryLinkSeparator aria-hidden>|</SummaryLinkSeparator>
              </>
            )}
            <SummaryLink type="button" onClick={onChangePlan}>
              {t("dueDiligence.newCaseModal.payment.summary.changePlan")}
            </SummaryLink>
          </SummaryActions>
        </SummaryBox>
      );
    }

    if (payment.kind === "plan" && cost !== undefined) {
      const plan = plans?.find((offer) => offer.lookupKey === payment.lookupKey);
      return plan ? (
        <SummaryBox $tone="ok">
          {t("dueDiligence.newCaseModal.payment.summary.deductedFromPlan", {
            count: cost,
            left: plan.monthlyCredits - cost,
          })}
        </SummaryBox>
      ) : null;
    }

    if (payment.kind === "oneOff" && oneOffPrice != null) {
      const oneOff = t("dueDiligence.newCaseModal.payment.summary.oneOff", { price: money(oneOffPrice) });
      if (cost === undefined) {
        return <SummaryBox $tone="info">{oneOff}</SummaryBox>;
      }
      const equivalent = money(cost * CREDIT_EURO_VALUE);
      return (
        <SummaryBox $tone="info">
          {oneOff} {t("dueDiligence.newCaseModal.payment.summary.withAPlan", { amount: equivalent })}
        </SummaryBox>
      );
    }

    return null;
  })();

  if (withCredits) {
    // Every tier stays selectable: what can't be paid shows in the summary
    // and blocks the button instead
    return (
      <div>
        <TierSelector
          selectedTier={tier}
          onTierChange={onTierChange}
          creditCosts={creditCosts}
          notes={notes}
        />
        {summary}
      </div>
    );
  }

  const unavailablePlans =
    plans && creditCosts
      ? plansUnavailableForTier(plans, tier, creditCosts, {
          notIncluded: t("dueDiligence.newCaseModal.payment.planTierNotIncluded"),
          notEnough: t("dueDiligence.newCaseModal.payment.planNotEnoughCredits"),
        })
      : {};

  return (
    <div>
      <TierSelector selectedTier={tier} onTierChange={onTierChange} pricing={pricing} notes={notes} />
      {summary}
      {!paysWithCredits(credits) &&
        plans &&
        plans.length > 0 && (
          <>
            <PlansTitle>{t("dueDiligence.newCaseModal.payment.plansTitle")}</PlansTitle>
            <DueDiligencePlanPicker
              plans={plans}
              selectedLookupKey={selectedPlan}
              onSelect={(lookupKey) => onPlanChange(lookupKey === selectedPlan ? null : lookupKey)}
              oneOffPrices={pricing}
              creditCosts={creditCosts}
              unavailablePlans={unavailablePlans}
            />
          </>
        )}
    </div>
  );
};
